import * as ents from '@attain-apps/entities'
import * as excel from '@attain-apps/excel'
import { Workbook } from '@attain-apps/fe/web/main/lib/excel'
import { TypeChecker } from '@attain-apps/type-guards'

import type { Entity } from '../types'

import type { ProposalInfo } from './proposal-info'

/**
 * Default column width by which {@link ents.extra.common.Report.Column.width}
 * is multiplied, when relevant
 *
 * __NOTE:__ when no column widths are set, we auto-size the columns based on
 * the content. So this is not used in that case
 */
const STANDARD_COLUMN_WIDTH = 20

/**
 * Columns with automatically set width should have a little padding to improve
 * readability
 */
const AUTO_SIZED_COLUMN_PADDING = 5

/**
 * Header rows use a larger font size
 */
const HEADER_FONT_SIZE = 16

/**
 * Default column width by which {@link ents.extra.common.Report.Column.width}
 * is multiplied, when relevant
 *
 * __NOTE:__ when no column widths are set, we auto-size the columns based on
 * the content. So this is not used in that case
 */
const MIN_HEADER_WIDTH = HEADER_FONT_SIZE * 6

/**
 * Determines how many empty rows appear below the report headers, above the
 * report content
 */
const NUM_EMPTY_ROWS_AFTER_HEADERS = 2

/**
 * Displayed to users when a report is missing content. This informs the user
 * that something has gone wrong with the given report
 */
const MISSING_CONTENT_PLACEHOLDER_ROWS: excel.Row[] = [
  new excel.Row([
    new excel.Cell('<------- Encountered error ------->')
      .fontItalic()
      .alignment({ horizontal: 'center' }),
  ]),
]

/**
 * TODO:
 */
export function getWorkbook(
  reports: readonly Entity[],
  info: ProposalInfo,
): Workbook {
  return new Workbook(reports.map(report => getSheet(report, info)))
}

/**
 * TODO:
 */
function getSheet(report: Entity, info: ProposalInfo): excel.Sheet {
  const { content: { rows, columns } = {} } = report

  /**
   * First few rows of the report, identifies what is in the report
   */
  const headers: readonly string[] = [
    info.tenantName,
    info.proposalTitle,
    report.label.name,
  ].filter(TypeChecker.isNotNullish.raw)
  const headerCells = headers.map(header => {
    const excelCell = new excel.Cell(header)
    excelCell.fontBold()
    excelCell.fontSize(HEADER_FONT_SIZE)
    excelCell.alignment({ horizontal: 'center' })
    return excelCell
  })

  // Starts with just header rows, more rows are added below
  const excelRows: excel.Row[] = headerCells.map(cell => new excel.Row([cell]))
  if (excelRows.length) {
    // If there are header rows, we add empty rows before the content
    for (let i = 0; i < NUM_EMPTY_ROWS_AFTER_HEADERS; i++) {
      excelRows.push(excel.Row.empty())
    }
  }

  // Represents the actual content of the reports
  const contentExcelRows =
    rows?.map(row => getRow(row)) ?? MISSING_CONTENT_PLACEHOLDER_ROWS
  excelRows.push(...contentExcelRows)

  // Creates the sheet
  const excelSheet = new excel.Sheet(report.label.code, excelRows)

  // Sets column span for the headers
  const maxNumColumns = Math.max(
    ...excelRows.map(excelRow => excelRow.cells.length),
  )
  for (const cell of headerCells) {
    cell.setColumnSpan(maxNumColumns)
  }

  // Sets column widths
  if (columns) {
    for (const [index, column] of columns.entries()) {
      // Applies the provided configuration
      excelSheet.setColumnWidth(
        index + 1,
        STANDARD_COLUMN_WIDTH * (column?.width ?? 1),
      )
    }
  } else {
    // Auto-sizes columns based on the content

    /**
     * Lookup used to associate each column index with its corresponding
     * maximum width
     */
    const maxWidthsByColumnNumber: Partial<Record<number, number>> = {}

    // Iterate through each row
    excelRows.forEach(excelRow => {
      // Iterate through each cell in the current row
      excelRow?.cells.forEach((excelCell, index) => {
        const currentWidth = excelCell?.value?.toString().length ?? 0
        if (!currentWidth) {
          // Empty cells will never increase the max width of the column
          return
        }
        const previousMaxWidth = maxWidthsByColumnNumber[index]
        const newMaxWidth = previousMaxWidth
          ? Math.max(previousMaxWidth, currentWidth)
          : currentWidth + AUTO_SIZED_COLUMN_PADDING
        // NOTE: we must always set something to ensure that this column has a
        // width assigned to it
        maxWidthsByColumnNumber[index] = newMaxWidth
      })
    })

    for (let i = 0; i < maxNumColumns; i++) {
      excelSheet.setColumnWidth(
        i + 1,
        maxWidthsByColumnNumber[i] ?? STANDARD_COLUMN_WIDTH,
      )
    }
  }

  const totalWidth = excelSheet.getTotalOfConfiguredColumnWidths()
  if (totalWidth < MIN_HEADER_WIDTH && maxNumColumns >= 1) {
    // If the total width is less than the minimum width for the headers, add
    // all of the missing width to the last column
    excelSheet.addColumnWidth(maxNumColumns, MIN_HEADER_WIDTH - totalWidth)
  }

  // Returns the final sheet
  return excelSheet
}

/**
 * Produces an {@link Row} from a {@link ents.extra.common.Report.Row}
 */
function getRow(row: ents.extra.common.Report.Row | undefined): excel.Row {
  if (!row) {
    return excel.Row.empty()
  }

  return new excel.Row(row.cells.map(cell => getCell(cell)))
}

/**
 * Produces an {@link Cell} from a {@link ents.extra.common.Report.Cell}
 */
function getCell(cell: ents.extra.common.Report.Cell | undefined): excel.Cell {
  if (TypeChecker.isNullish(cell)) {
    return excel.Cell.empty()
  }

  const { align, columnSpan, content, indents, style } = cell
  // TODO: maybe should handle this converting 'content.raw' value to CellValue
  // type on some other way
  const xlsCell = new excel.Cell(content?.raw.toString())
  xlsCell.setValueType(
    cell.content?.type && excel.convertValueType(cell.content.type),
  )
  xlsCell.setNumberFormat(excel.NumberFormat.from(cell.content))
  const styleModifier = style && CELL_STYLE_MODIFIERS[style]
  styleModifier?.(xlsCell)
  const alignmentModifier = align && CELL_ALIGNMENT_MODIFIERS[align]
  alignmentModifier?.(xlsCell)
  xlsCell.alignment({ indent: indents })
  if (columnSpan) {
    xlsCell.setColumnSpan(columnSpan)
  }

  return xlsCell
}

/**
 * TODO:
 */
const CELL_STYLE_MODIFIERS: Record<
  ents.extra.common.Report.Cell.Style,
  (cell: excel.Cell) => void
> = {
  [ents.extra.common.Report.Cell.Style.GrandTotal]: (cell: excel.Cell) =>
    cell
      .fontBold()
      .borderBottom({ style: 'double' })
      .borderTop({ style: 'thin' }),
  [ents.extra.common.Report.Cell.Style.Label]: (cell: excel.Cell) =>
    cell.fontBold(),
  [ents.extra.common.Report.Cell.Style.ColumnHeader]: (cell: excel.Cell) =>
    cell
      .fontBold()
      .borderBottom({ style: 'thin' })
      .borderTop({ style: 'thin' })
      .wrapText(true)
      .fontSize(12),
  [ents.extra.common.Report.Cell.Style.Divider]: (cell: excel.Cell) =>
    cell.borderBottom({ style: 'thin' }),
  [ents.extra.common.Report.Cell.Style.ReportHeader]: (cell: excel.Cell) =>
    cell.fontBold().alignment({ horizontal: 'center' }).fontSize(16),
  [ents.extra.common.Report.Cell.Style.SectionHeader]: (cell: excel.Cell) =>
    cell.solidBackground('000000').fontColor('FFFFFF'),
  [ents.extra.common.Report.Cell.Style.SubsectionHeader]: (cell: excel.Cell) =>
    cell.solidBackground('EAEBEC'),
  [ents.extra.common.Report.Cell.Style.Subtotal]: (cell: excel.Cell) =>
    cell
      .fontBold()
      .borderTop({ style: 'thin' })
      .borderBottom({ style: 'thin' }),
}

/**
 * TODO:
 */
const CELL_ALIGNMENT_MODIFIERS: Record<
  ents.extra.common.Report.Cell.TextAlignment,
  (cell: excel.Cell) => void
> = {
  [ents.extra.common.Report.Cell.TextAlignment.Center]: (cell: excel.Cell) =>
    cell.alignment({ horizontal: 'center' }),
  [ents.extra.common.Report.Cell.TextAlignment.Left]: (cell: excel.Cell) =>
    cell.alignment({ horizontal: 'left' }),
  [ents.extra.common.Report.Cell.TextAlignment.Right]: (cell: excel.Cell) =>
    cell.alignment({ horizontal: 'right' }),
}
